import React, { useRef, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { getChartColors, isPartnerData, DEFAULT_CHART_COLORS } from "../utils/partnerColors";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

/**
 * The subtitle's "Total: …" must not print the raw float sum — a column of
 * one-decimal hours would report "Total: 948.6999999999999". Sum, then round
 * to 2 dp and drop trailing zeros — no chart here carries meaning below
 * cents, and the artifact digits were never real precision.
 */
const formatTotal = (values) => {
  const sum = values.reduce((a, b) => a + (Number(b) || 0), 0);
  return String(Number(sum.toFixed(2)));
};

/**
 * Counts have no fractional values, but Chart.js picks its own tick spacing
 * from the data range alone — a single-bar "1" series lands on 0.1 steps and
 * reads as if a partner could be 0.3 late accounts. Whenever every plotted
 * value is a whole number, pin the value axis to whole-number ticks; hour and
 * rate series keep their decimals.
 */
const isWholeNumberSeries = (chartData) => {
  const datasets = chartData?.datasets;
  if (!Array.isArray(datasets) || datasets.length === 0) return false;
  let seen = 0;
  for (const ds of datasets) {
    const values = Array.isArray(ds?.data) ? ds.data : [];
    for (const v of values) {
      if (v === null || v === undefined || v === "") continue;
      const n = Number(v);
      if (!Number.isFinite(n) || !Number.isInteger(n)) return false;
      seen += 1;
    }
  }
  return seen > 0;
};

/** Value axis is x on horizontal bars (indexAxis "y"), y everywhere else. */
const withWholeNumberTicks = (opts) => {
  const scales = opts.scales;
  if (!scales || Object.keys(scales).length === 0) return opts;
  const axis = opts.indexAxis === "y" ? "x" : "y";
  const scale = scales[axis] || {};
  const ticks = scale.ticks || {};
  if (ticks.precision !== undefined || ticks.stepSize !== undefined) return opts;
  return {
    ...opts,
    scales: {
      ...scales,
      [axis]: { ...scale, ticks: { ...ticks, precision: 0 } },
    },
  };
};

const ChartContainer = ({ title, data, type = "bar", height = 300, preserveOrder = false, backgroundColor, borderColor, options: customOptions, multiDataset = false, stacked = false, subtitle }) => {
  const chartRef = useRef();

  const colors = DEFAULT_CHART_COLORS;

  const processData = () => {
    if (!data || typeof data !== "object") {
      return {
        labels: [],
        datasets: [],
      };
    }

    // If data already has the Chart.js format (labels and datasets), use it directly
    if (multiDataset && data.labels && data.datasets) {
      return data;
    }

    // Special handling for multiline charts (timeline data)
    if (type === "multiline" && Array.isArray(data)) {
      return processMultilineData(data);
    }

    // For line charts or when preserveOrder is true, preserve the original order
    // For other charts, sort by values in descending order
    let entries;
    if (type === "line" || preserveOrder) {
      entries = Object.entries(data); // Preserve order for time series
    } else {
      entries = Object.entries(data).sort((a, b) => b[1] - a[1]); // Sort by values
    }

    const labels = entries.map((entry) => entry[0]);
    const values = entries.map((entry) => entry[1]);

    // Get appropriate colors for the data (use sorted labels order)
    const chartColors = getChartColors(labels, title, type);

    if (type === "doughnut" || type === "pie") {
      return {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: chartColors?.backgroundColor || colors.slice(0, labels.length).map(color => color + "CC"),
            borderColor: chartColors?.borderColor || colors.slice(0, labels.length),
            borderWidth: chartColors?.borderWidth || 2,
          },
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: title,
          data: values,
          backgroundColor: backgroundColor || chartColors?.backgroundColor || (type === "line" ? "transparent" : colors[0] + "80"),
          borderColor: borderColor || chartColors?.borderColor || colors[0],
          borderWidth: chartColors?.borderWidth || 2,
          fill: type === "line" ? false : true,
          tension: type === "line" ? 0.4 : 0,
        },
      ],
    };
  };

  const processMultilineData = (timelineData) => {
    if (!timelineData || timelineData.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Extract labels (months)
    const labels = timelineData.map(item => item.month);
    
    // Get all partners from the first data point
    const firstDataPoint = timelineData[0];
    const partners = Object.keys(firstDataPoint.partners || {});
    
    // Create datasets for each partner
    const datasets = [];
    
    // Add partner datasets
    partners.forEach((partner, index) => {
      const partnerData = timelineData.map(item => item.partners[partner] || 0);
      const partnerColors = getChartColors([partner], title, 'line');
      
      datasets.push({
        label: partner,
        data: partnerData,
        borderColor: partnerColors?.borderColor || colors[index % colors.length],
        backgroundColor: 'transparent',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
      });
    });

    // Add signed this month dataset if available
    const signedThisMonthData = timelineData.map(item => item.signedThisMonth || 0);
    const hasSignedSeries = signedThisMonthData.some(value => value > 0);

    if (hasSignedSeries) {
      datasets.push({
        label: 'Signed This Month',
        data: signedThisMonthData,
        borderColor: '#10b981',
        backgroundColor: 'transparent',
        borderWidth: 2,
        fill: false,
        tension: 0.35,
        pointRadius: 3,
        pointHoverRadius: 6,
        borderDash: [4, 4],
      });
    }
    
    // Add total dataset
    const totalData = timelineData.map(item => item.total || 0);
    datasets.push({
      label: 'Total',
      data: totalData,
      borderColor: '#1f2937', // Dark gray for total
      backgroundColor: 'transparent',
      borderWidth: 3,
      fill: false,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 8,
      borderDash: [5, 5], // Dashed line for total
    });
    
    return { labels, datasets, partnerCount: partners.length, hasSignedSeries };
  };

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: type === "doughnut" || type === "pie" ? "right" : "top",
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "white",
        bodyColor: "white",
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
      },
    },
    scales:
      type === "doughnut" || type === "pie"
        ? {}
        : {
            y: {
              beginAtZero: true,
              stacked: stacked,
              grid: {
                color: "rgba(0, 0, 0, 0.1)",
              },
              ticks: {
                color: "#6B7280",
              },
            },
            x: {
              stacked: stacked,
              grid: {
                display: false,
              },
              ticks: {
                color: "#6B7280",
                maxRotation: 45,
              },
            },
          },
  };

  const chartData = processData();

  // Merge custom options with default options
  const mergedOptions = customOptions ? { ...defaultOptions, ...customOptions } : defaultOptions;
  const options = isWholeNumberSeries(chartData)
    ? withWholeNumberTicks(mergedOptions)
    : mergedOptions;

  const renderChart = () => {
    if (chartData.labels.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <p>No data available</p>
        </div>
      );
    }

    switch (type) {
      case "line":
        return <Line ref={chartRef} data={chartData} options={options} />;
      case "multiline":
        return <Line ref={chartRef} data={chartData} options={options} />;
      case "doughnut":
        return <Doughnut ref={chartRef} data={chartData} options={options} />;
      case "pie":
        return <Doughnut ref={chartRef} data={chartData} options={options} />;
      default:
        return <Bar ref={chartRef} data={chartData} options={options} />;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        {chartData.labels.length > 0 && (
          <p className="text-sm text-gray-500 mt-1">
            {subtitle ? subtitle :
            type === "multiline" ?
              `${chartData.partnerCount ?? (chartData.datasets.length - 1 - (chartData.hasSignedSeries ? 1 : 0))} partners • ${chartData.labels.length} months` :
              multiDataset && chartData.datasets ? 
                `${chartData.datasets.length} divisions • ${chartData.labels.length} months` :
                `${chartData.labels.length} categories • Total: ${formatTotal(Object.values(data))}`
            }
          </p>
        )}
      </div>

      <div style={{ height: `${height}px` }}>{renderChart()}</div>
    </div>
  );
};

export default ChartContainer;
